export interface User {
  id: string;
  username: string;
  role: string;
}

export interface MeResponse {
  user: User;
  csrfToken: string;
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

let onUnauthorized: (() => void) | null = null;

/** Register a handler called when a non-auth API call returns 401 — the app uses
 *  it to drop the cached session and bounce to /login on mid-session expiry. */
export function setOnUnauthorized(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

// 401s from these are normal control flow, not session expiry: login 401 = wrong
// credentials, me 401 = logged out (RequireAuth decides), logout 401 = double-click.
const AUTH_ENDPOINTS = new Set(['/api/auth/login', '/api/auth/me', '/api/auth/logout']);

function notifyUnauthorized(path: string): void {
  if (onUnauthorized && !AUTH_ENDPOINTS.has(path)) onUnauthorized();
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {};
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const isMutating = method !== "GET" && method !== "HEAD" && method !== "OPTIONS";
  if (isMutating && csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }
  const res = await fetch(path, {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  if (res.status === 401) notifyUnauthorized(path);
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; message?: string; details?: unknown };
    throw new ApiError(res.status, err.error ?? "HTTP_ERROR", err.message ?? res.statusText, err.details);
  }
  return data as T;
}

/** Multipart POST (file uploads) — CSRF header included, no Content-Type (browser sets the boundary). */
export async function apiForm<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const res = await fetch(path, { method: "POST", headers, body: form });
  if (res.status === 401) notifyUnauthorized(path);
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; message?: string; details?: unknown };
    throw new ApiError(res.status, err.error ?? "HTTP_ERROR", err.message ?? res.statusText, err.details);
  }
  return data as T;
}
